import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { useToast } from '@/components/ui/toast';
import { CheckCircle, KeyRound } from 'lucide-react';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      toast({ title: 'エラー', description: 'パスワードが一致しません', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      await api.updatePassword(password);
      setSuccess(true);
    } catch (err) {
      toast({
        title: 'パスワード更新失敗',
        description: err instanceof Error ? err.message : 'エラーが発生しました',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950 p-3 sm:p-4">
      <div className="w-full max-w-md space-y-4 sm:space-y-6">
        <div className="text-center space-y-2 sm:space-y-3">
          <img src="/logo.svg" alt="Hitorigoto" className="h-12 sm:h-16 mx-auto dark:hidden" />
          <img src="/logo-dark.svg" alt="Hitorigoto" className="h-12 sm:h-16 mx-auto hidden dark:block" />
        </div>

        <Card>
          {success ? (
            <CardContent className="py-8">
              <div className="text-center space-y-3">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                <h3 className="text-lg font-semibold">パスワードを更新しました</h3>
                <p className="text-sm text-muted-foreground">
                  新しいパスワードでログインできます。
                </p>
                <Button className="mt-4" onClick={() => navigate('/login')}>
                  ログインへ
                </Button>
              </div>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="pt-6 space-y-4">
                <div className="text-center space-y-2 mb-2">
                  <KeyRound className="h-10 w-10 text-primary mx-auto" />
                  <h3 className="text-lg font-semibold">新しいパスワードを設定</h3>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="new-password">新しいパスワード</label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="8文字以上"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="new-password-confirm">パスワード（確認）</label>
                  <Input
                    id="new-password-confirm"
                    type="password"
                    placeholder="もう一度入力"
                    value={passwordConfirm}
                    onChange={e => setPasswordConfirm(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? '更新中...' : 'パスワードを更新'}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
